import { Message, MessageOption, OptionsSource } from "./types";

/** Technical user turns (`__START__`, JSON submissions) are never rendered. */
export const isTechnicalMessage = (
  message: Pick<Message, "role" | "content">
) => {
  if (message.role !== "user") return false;
  const content = message.content.trim();
  return content.startsWith("__") || content.startsWith("{");
};

const normalised = (text: string) => text.trim().replace(/\s+/g, " ");

/**
 * `messages` with Ori's opening line where chat showed it (after the message
 * `after` names), unless history has since brought it back there: Coach saves
 * the line just before the message the learner sent under it.
 */
export const withOpeningLine = (
  messages: Message[],
  line: { text: string } | null,
  after: { id: string | null } | null
): Message[] => {
  if (!line || !after) return messages;
  // Under the message the chat stood at, found by its id: a reconcile can
  // reshape the transcript around it. With no id, the transcript was empty.
  const at =
    after.id === null
      ? 0
      : messages.findIndex((message) => message.message_id === after.id) + 1;
  if (after.id !== null && at === 0) return messages;
  // Coach saves the line as the very message after the anchor, with its own
  // id; history names no line id, so it is matched there by its words. A
  // later reply of Ori's that happens to say the same is not the line.
  const there = messages[at];
  const saved =
    there?.role === "assistant" &&
    there.message_id !== undefined &&
    normalised(there.content) === normalised(line.text);
  if (saved) return messages;
  return [
    ...messages.slice(0, at),
    { role: "assistant", content: line.text },
    ...messages.slice(at),
  ];
};

/** The same five the new web app and mobile offer. */
export const GENERIC_STARTER_IDS = [
  "stressfulSituation",
  "sleepBetter",
  "recommendSession",
  "scoresMeaning",
  "stressPatterns",
] as const;

export type GenericStarterId = typeof GENERIC_STARTER_IDS[number];

/**
 * A Fisher–Yates shuffle, so each display of the generics has its own order
 * and tap data isn't position-biased. Analytics carries the starter id and
 * its displayed position.
 */
export const shuffled = <T>(
  items: readonly T[],
  random: () => number = Math.random
): T[] => {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

interface SelectGenericStartersArgs {
  /** Flag on, and nothing else holds the starters back (draft, view, QA). */
  enabled: boolean;
  isLoadingHistory: boolean;
  isSending: boolean;
  isConnected: boolean;
  /** Coach said this conversation opens with Ori's greeting. */
  opensWithGreeting: boolean;
  hasEverMessaged: boolean;
  messages: Pick<Message, "role" | "content" | "message_id" | "options">[];
  /** The learner typed past them. */
  dismissed: boolean;
  /** This display is under way: they were shown and have not really gone. */
  held: boolean;
}

/**
 * - `on`: on screen.
 * - `held`: off screen for now, but this display (its order, its impression)
 *   carries on if they come back.
 * - `off`: gone; a later display is a new one.
 */
export type GenericStartersState = "on" | "held" | "off";

/**
 * Whether a learner who has never messaged Ori gets the generic starters:
 * Coach writes them none of their own. They first appear on a live chat, under
 * Ori's greeting once it has arrived or on the welcome when Coach is not
 * greeting, so never in place of a greeting that failed. Once shown they ride
 * out a dropped connection or a send in flight, and step aside while the
 * learner's own message waits for Coach (a lock-rejected send can still come
 * back). They go for good when Coach has the learner's message, the learner
 * types past them, or the view moves on; never under a message offering its
 * own choices.
 */
export const selectGenericStarters = ({
  enabled,
  isLoadingHistory,
  isSending,
  isConnected,
  opensWithGreeting,
  hasEverMessaged,
  messages,
  dismissed,
  held,
}: SelectGenericStartersArgs): GenericStartersState => {
  if (!enabled || isLoadingHistory || hasEverMessaged || dismissed) {
    return "off";
  }
  const spoken = messages.filter((message) => !isTechnicalMessage(message));
  const learnerTurns = spoken.filter((message) => message.role === "user");
  // Only Coach's copy of a message carries an id; ours is still in flight.
  if (learnerTurns.some((message) => message.message_id)) return "off";
  const newest = spoken[spoken.length - 1];
  if (newest?.options?.length) return "off";
  if (learnerTurns.length > 0) return held ? "held" : "off";
  if (held) return "on";
  return isConnected &&
    !isSending &&
    (newest !== undefined || !opensWithGreeting)
    ? "on"
    : "off";
};

interface SelectDynamicStartersArgs {
  /** Flag on, and nothing else holds the starters back (draft, view, QA). */
  enabled: boolean;
  isLoadingHistory: boolean;
  /** A turn is in flight, including Ori's reply still streaming. */
  isSending: boolean;
  messages: Pick<
    Message,
    "role" | "content" | "message_id" | "options" | "options_source"
  >[];
  /** The message whose starters the learner typed past. */
  dismissedMessageId: string | null;
}

export interface DynamicStarters {
  messageId: string;
  options: MessageOption[];
  source: OptionsSource;
}

/**
 * The options on Ori's newest message, when Coach marked them as starters (a
 * source), whoever the learner is. Options without one are the model's own
 * and never render.
 */
export const selectDynamicStarters = ({
  enabled,
  isLoadingHistory,
  isSending,
  messages,
  dismissedMessageId,
}: SelectDynamicStartersArgs): DynamicStarters | null => {
  if (!enabled || isLoadingHistory || isSending) return null;

  let newest: SelectDynamicStartersArgs["messages"][number] | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (!isTechnicalMessage(messages[i])) {
      newest = messages[i];
      break;
    }
  }
  if (
    !newest ||
    newest.role !== "assistant" ||
    !newest.message_id ||
    !newest.options?.length ||
    !newest.options_source ||
    newest.message_id === dismissedMessageId
  ) {
    return null;
  }

  return {
    messageId: newest.message_id,
    options: newest.options,
    source: newest.options_source,
  };
};
